import { Link } from "react-router-dom";
import { Users, Star, Languages, Presentation, ClipboardList, ArrowUpRight } from "lucide-react";
import { Reveal, Overline, MaskReveal } from "@/components/Motion";
import { SEO } from "@/components/SEO";
import { ASSETS } from "@/lib/assets";

const ROLES = [
  { icon: Users, title: "Hostesses", desc: "Polished, professional exhibition hosts who welcome visitors, manage flow and represent your brand with warmth and poise." },
  { icon: Star, title: "Brand Ambassadors", desc: "Charismatic ambassadors who embody your brand values and turn passing interest into meaningful engagement." },
  { icon: Languages, title: "Multilingual Staff", desc: "Experienced male and female staff across nationalities and languages, ready to connect with a truly international audience." },
  { icon: Presentation, title: "Product Specialists", desc: "Trained specialists who deliver confident product presentations and answer technical questions with authority." },
  { icon: ClipboardList, title: "Lead Collection Support", desc: "Diligent staff to capture, qualify and organise leads so your team leaves every show with real business value." },
];

const Staffing = () => (
  <div data-testid="staffing-page">
    <SEO title="Exhibition Staffing & Brand Ambassadors | EVO Exhibitions" description="Professional multilingual exhibition hostesses, brand ambassadors and product specialists for events across Dubai and the Middle East." path="/staffing" />
    {/* Hero */}
    <section className="px-6 md:px-12 lg:px-20 pt-40 md:pt-48 pb-16">
      <Reveal><Overline className="block mb-8">Exhibition Staffing & Brand Ambassadors</Overline></Reveal>
      <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] text-evo-pearl max-w-4xl">
        <MaskReveal delay={0.1}>The people who bring</MaskReveal>
        <MaskReveal delay={0.25}>your stand <span className="text-evo-gold italic">to life</span>.</MaskReveal>
      </h1>
      <Reveal delay={0.3}>
        <p className="mt-10 text-evo-ash font-light max-w-xl leading-relaxed">
          EVO provides professional exhibition hosts and brand ambassadors for international events. Our multilingual team of experienced male and female staff is trained to represent your brand with polish, energy and full commitment to your show schedule.
        </p>
      </Reveal>
    </section>

    {/* Roles */}
    <section className="px-6 md:px-12 lg:px-20 pb-28">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {ROLES.map((r, i) => {
          const Icon = r.icon;
          const span = i < 2 ? "md:col-span-6" : "md:col-span-4";
          return (
            <Reveal key={r.title} delay={(i % 3) * 0.08} className={span}>
              <div data-testid={`staffing-role-${i}`} className="group h-full bg-evo-marble1 border border-white/5 hover:border-evo-gold/30 transition-colors duration-500 p-8 md:p-10">
                <Icon size={26} className="text-evo-gold mb-16" strokeWidth={1.4} />
                <h2 className="font-serif text-2xl md:text-3xl text-evo-pearl mb-3 group-hover:text-evo-gold transition-colors duration-300">{r.title}</h2>
                <p className="text-evo-ash text-sm leading-relaxed font-light">{r.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>

    {/* Assurance band */}
    <section className="relative overflow-hidden border-y border-white/5">
      <img src={ASSETS.marble} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-evo-black/75" />
      <div className="relative z-10 px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <Reveal>
          <h2 className="font-serif font-light text-3xl md:text-5xl tracking-tight text-evo-pearl max-w-3xl text-balance">
            Professional appearance, sharp communication, and <span className="text-evo-gold italic">total commitment</span> to your show.
          </h2>
        </Reveal>
      </div>
    </section>

    {/* CTA */}
    <section className="px-6 md:px-12 lg:px-20 py-28 md:py-32">
      <div className="bg-evo-marble1 border border-white/5 p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
        <h2 className="font-serif font-light text-3xl md:text-5xl text-evo-pearl max-w-xl">Need a team for your next exhibition?</h2>
        <Link to="/contact" data-testid="staffing-cta-btn" className="inline-flex items-center gap-3 bg-evo-gold text-evo-black hover:bg-evo-goldsoft transition-colors duration-300 px-9 py-5 text-xs uppercase tracking-[0.2em] group shrink-0">
          Request Staff
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>
    </section>
  </div>
);

export default Staffing;
